import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_URL } from 'src/app/core/constants/api';
import { environment } from 'src/environments/environment';
import {
  IMasterPlaceholderResponse,
  IPublishedFormResponse,
  ITemplatePayload,
  ITemplateCreateResponse,
  IWhatsAppMediaResponse,
  IUploadEmailResponse,
  ItemplateLayoutResponse,
  ItemplateContentResponse,
  IRCSAgentsResponse,
  IPublishedForm,
  IDeliveryLogsResponse,
  ILogsPayload,
} from '../interfaces/create-template.interface';
import {
  EditActionTemplateCheck,
  ITemplateDeleteResponse,
  ITemplateFilteredData,
  ITemplateListResponse,
  ITestTemplateDataResponse,
  ITestTemplateResponse,
  TemplateListRequest,
  UpdateTemplateActionStatus,
  UpdateTemplateStatus,
} from '../interfaces/template-list-response.interface';
import {
  CategoryChange,
  IDLTIdResponse,
  IRCSTemplateConfigResponse,
  ITemplateConfigResponse,
} from 'src/app/shared/interfaces/common-api.interface';

@Injectable({ providedIn: 'root' })
export class TemplateService {
  selTemplateData: ITemplateFilteredData;
  bubbleHashtags: string[] = [];
  categoryChangeData: CategoryChange[] = [];

  constructor(private readonly http: HttpClient) {}

  get templateData(): ITemplateFilteredData {
    return this.selTemplateData;
  }
  set templateData(templateData: ITemplateFilteredData) {
    this.selTemplateData = templateData;
  }

  getTemplateListing(
    payload: TemplateListRequest,
  ): Observable<ITemplateListResponse> {
    return this.http.post<ITemplateListResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.TEMPLATES_FILTERED_LIST}`,
      payload,
    );
  }

  getTemplateConfigs(channel: string): Observable<ITemplateConfigResponse> {
    return this.http.get<ITemplateConfigResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.TEMPLATE_CONFIG}?channel=${channel}`,
    );
  }

  getRCSTemplateConfigs(
    channel: string,
  ): Observable<IRCSTemplateConfigResponse> {
    return this.http.get<IRCSTemplateConfigResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.TEMPLATE_CONFIG}?channel=${channel}`,
    );
  }

  deleteDraftTemplate(
    templateId: string,
    channel: string,
  ): Observable<ITemplateDeleteResponse> {
    return this.http.delete<ITemplateDeleteResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.TEMPLATES}?code=${templateId}&channel=${channel}`,
    );
  }

  getMasterPlaceholders(
    channelType: string,
  ): Observable<IMasterPlaceholderResponse> {
    return this.http.get<IMasterPlaceholderResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.PLACEHOLDERS_MASTER}?type=${channelType}&active=true`,
    );
  }

  getFormList(): Observable<IPublishedFormResponse> {
    return this.http.get<IPublishedFormResponse>(
      `${environment.FORM_BASE_URL}${API_URL.LIST}?formStatus=PUBLISHED`,
    );
  }

  uploadApprovedEmail(payload: FormData): Observable<IUploadEmailResponse> {
    return this.http.post<IUploadEmailResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.UPLOAD_APPROVED_EMAIL}`,
      payload,
    );
  }

  uploadRingiFile(payload: FormData): Observable<IUploadEmailResponse> {
    return this.http.post<IUploadEmailResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.UPLOAD_APPROVED_EMAIL}?component=campaign`,
      payload,
    );
  }

  downloadApprovedEmail(preCheckPath: string) {
    const url = `${environment.CAMPAIGN_BASE_URL}${API_URL.DOWNLOAD_APPROVAL}?preCheckPath=${preCheckPath}`;
    return this.http.get(url);
  }

  uploadWhatsAppMedia(payload: FormData): Observable<IWhatsAppMediaResponse> {
    return this.http.post<IWhatsAppMediaResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.UPLOAD_TEMP_MEDIA}`,
      payload,
    );
  }

  uploadCampaignMedia(payload: any): Observable<IWhatsAppMediaResponse> {
    return this.http.post<IWhatsAppMediaResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.UPLOAD_TEMP_MEDIA}`,
      payload,
    );
  }

  createTemplate(
    payload: ITemplatePayload,
  ): Observable<ITemplateCreateResponse> {
    return this.http.post<ITemplateCreateResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.TEMPLATE_REGISTER}`,
      payload,
    );
  }

  updateTemplate(
    payload: ITemplatePayload,
  ): Observable<ITemplateCreateResponse> {
    return this.http.put<ITemplateCreateResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.TEMPLATE_EMAIL_MODIFY}`,
      payload,
    );
  }

  updateTemplateStatus(payload: {
    templateCode: string;
    status: string;
    statusReason: string;
  }): Observable<UpdateTemplateActionStatus> {
    return this.http.patch<UpdateTemplateActionStatus>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.UPDATE_TEMPLATE_STATUS}`,
      payload,
    );
  }

  checkEditAction(templateCode: string): Observable<EditActionTemplateCheck> {
    return this.http.get<EditActionTemplateCheck>(
      `${environment.CAMPAIGN_BASE_URL}template/${API_URL.CHECK_TEMPLATE_EDIT_ACTION}?templateCode=${templateCode}`,
    );
  }

  refreshStatus(
    payload: string[],
    channel: string,
  ): Observable<UpdateTemplateStatus> {
    return this.http.patch<UpdateTemplateStatus>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.REFRESH_STATUS}`,
      { templateCodes: payload, channel: channel },
    );
  }

  setBubbleMessageHashtags(tags: string[]): void {
    this.bubbleHashtags = [...tags];
  }

  getBubbleMessageHashtags(): string[] {
    return this.bubbleHashtags;
  }

  removeItemsFromList(a: string[], b: string[]) {
    return a.filter((el) => !b.includes(el));
  }

  hasDuplicates(array: string[]) {
    return new Set(array).size !== array.length;
  }

  getTestTemplate(templateCode: string): Observable<ITestTemplateDataResponse> {
    return this.http.get<ITestTemplateDataResponse>(
      `${environment.CAMPAIGN_BASE_URL}template/${API_URL.TEST_TEMPLATE}?templateCode=${templateCode}`,
    );
  }

  saveTestTemplate(payload: any): Observable<ITestTemplateResponse> {
    return this.http.post<ITestTemplateResponse>(
      `${environment.CAMPAIGN_BASE_URL}template/${API_URL.SAVE_TEST_TEMPLATE}`,
      payload,
    );
  }

  getEmailTemplateLayouts(): Observable<ItemplateLayoutResponse> {
    return this.http.get<ItemplateLayoutResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.EMAIL_TEMPLATE_LAYOUTS}`,
    );
  }

  getRCSAgents(category: string, bsp: string): Observable<IRCSAgentsResponse> {
    return this.http.get<IRCSAgentsResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.GET_RCS_AGENTS}?type=${category}&bsp=${bsp}`,
    );
  }

  uploadEmailMedia(
    mediaUploadRequest: any,
    file: File,
  ): Observable<IWhatsAppMediaResponse> {
    const formData = new FormData();
    formData.append('mediaFiles', file as Blob);
    formData.append('mediaUploadRequest', JSON.stringify(mediaUploadRequest));
    return this.http.post<IWhatsAppMediaResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.UPLOAD_TEMP_MEDIA}`,
      formData,
    );
  }

  getEmailemplateContent(
    templateCode: string,
  ): Observable<ItemplateContentResponse> {
    return this.http.get<ItemplateContentResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.EMAIL_TEMPLATE_CONTENT}?templateCode=${templateCode}`,
    );
  }

  saveEmailTemplate(payload: any): Observable<ITemplateCreateResponse> {
    return this.http.post<ITemplateCreateResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.SAVE_EMAIL_TEMPLATE}`,
      payload,
    );
  }

  addFormCodeTolist(formList: IPublishedForm[]): IPublishedForm[] {
    return formList.map((form) => ({
      ...form,
      formName: `${form.formName} (${form.formCode})`,
      name: form.formName,
    }));
  }

  isImageLargeEnough(
    fileOrBlob: File | Blob,
    minWidth: number,
    minHeight: number,
    tolerancePx: number = 0,
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const cleanAndResolve = (result: boolean) => {
        if (img.src.startsWith('blob:')) URL.revokeObjectURL(img.src);
        resolve(result);
      };
      img.onload = () => {
        const w = img.width;
        const h = img.height;

        // Accept if both dimensions are within tolerance
        const enoughWidth = w >= minWidth - tolerancePx;
        const enoughHeight = h >= minHeight - tolerancePx;
        cleanAndResolve(enoughWidth && enoughHeight);
      };
      img.onerror = () => {
        // Fallback via FileReader → DataURL
        const reader = new FileReader();
        reader.onloadend = () => {
          img.src = reader.result as string;
        };
        reader.readAsDataURL(fileOrBlob);
      };
      img.src = URL.createObjectURL(fileOrBlob);
    });
  }

  checkDuplicateDltId(dltId: string): Observable<IDLTIdResponse> {
    return this.http.get<IDLTIdResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.DLT_ID}?dltId=${dltId}`,
    );
  }

  getDeliveryLogs(payload: ILogsPayload): Observable<IDeliveryLogsResponse> {
    return this.http.post<IDeliveryLogsResponse>(
      `${environment.BASE_URL}/reporting/v1/${API_URL.DELIVERY_LOGS}`,
      payload,
    );
  }

  validateGifDimensions(
    file: File,
    minWidth: number,
    minHeight: number,
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      // GIF header (6 bytes) + Logical Screen Descriptor (7 bytes)
      const headerSlice = file.slice(0, 13);
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const buffer = reader.result as ArrayBuffer;
          const bytes = new Uint8Array(buffer);

          if (bytes.length < 10) {
            return reject('Invalid GIF file.');
          }

          // Validate GIF signature (GIF87a or GIF89a)
          const signature =
            String.fromCharCode(bytes[0]) +
            String.fromCharCode(bytes[1]) +
            String.fromCharCode(bytes[2]) +
            String.fromCharCode(bytes[3]) +
            String.fromCharCode(bytes[4]) +
            String.fromCharCode(bytes[5]);

          if (signature !== 'GIF87a' && signature !== 'GIF89a') {
            return reject('Invalid file format. Please upload a valid GIF.');
          }

          // Width & Height (Little Endian)
          const width = bytes[6] | (bytes[7] << 8);
          const height = bytes[8] | (bytes[9] << 8);

          resolve(width >= minWidth && height >= minHeight);
        } catch (err) {
          reject('Error validating GIF dimensions.');
        }
      };

      reader.onerror = () => reject('File reading failed.');

      reader.readAsArrayBuffer(headerSlice);
    });
  }
}
